import { useState, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, Save } from "lucide-react";
import PatientSelector from "./PatientSelector";

const quillModules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

export default function NoteForm({ note, patients, onSave, onClose, isLoading }) {
  const [formData, setFormData] = useState({
    patientId: "",
    serviceId: "",
    consultation_notes: "",
    diagnosis: "",
    treatment_plan: "",
    date: "",
  });

  useEffect(() => {
    if (note) {
      setFormData({
        patientId: note.patientId || "",
        serviceId: note.serviceId || "",
        consultation_notes: note.consultation_notes || "",
        diagnosis: note.diagnosis || "",
        treatment_plan: note.treatment_plan || "",
        date: note.date
          ? new Date(note.date).toISOString().split("T")[0]
          : "",
      });
    }
  }, [note]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };


  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-4xl bg-white/90 backdrop-blur-xl border-slate-200/60 shadow-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-white/80 backdrop-blur-sm z-10 border-b">
          <CardTitle>{note ? "Edit" : "New"} Clinical Note</CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <PatientSelector
                patients={patients}
                selectedPatient={formData.patientId}
                onPatientChange={(val) => handleInputChange("patientId", val)}
                showAll={false}
              />
              <div className="space-y-2">
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleInputChange("date", e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Clinical Notes */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="consultation_notes">Consultation Notes</Label>
                <ReactQuill
                  theme="snow"
                  modules={quillModules}
                  value={formData.consultation_notes}
                  onChange={(val) => handleInputChange("consultation_notes", val)}
                  placeholder="Document the consultation details, patient symptoms, examination findings..."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="diagnosis">Diagnosis</Label>
                <ReactQuill
                  theme="snow"
                  modules={quillModules}
                  value={formData.diagnosis}
                  onChange={(val) => handleInputChange("diagnosis", val)}
                  placeholder="Clinical diagnosis and assessment..."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="treatment_plan">Treatment Plan</Label>
                <ReactQuill
                  theme="snow"
                  modules={quillModules}
                  value={formData.treatment_plan}
                  onChange={(val) => handleInputChange("treatment_plan", val)}
                  placeholder="Treatment recommendations, medications, follow-up instructions..."
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-6">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-gradient-to-r from-blue-600 to-indigo-600"
                disabled={isLoading}
              >
                <Save className="w-4 h-4 mr-2" /> 
                {isLoading ? "Saving..." : "Save Note"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
